import { useSelector } from 'react-redux';
import { devicesApi, useGetConnectedDevicesQuery } from 'services/devices/devices.api';

// The only endpoints that deliver device data points — both patch them into the
// `getConnectedDevices` cache (services/devices/devices.api.js) — so these are the only
// requests a device status may wait on. Keep the names here and nowhere else.
//
// `getDeviceLatestData` is the page-load bootstrap: `getConnectedDevices` dispatches it
// with no ids as soon as the device list lands and merges one newest row per device, so a
// pending one covers every device. `getNodeDataByTimestamps` is the on-demand range fetch
// a graph, datasheet or export makes, and it always names its ids.
const DEVICE_DATA_ENDPOINTS = ['getDeviceLatestData', 'getNodeDataByTimestamps'];

// A name that matches no endpoint silently disables the whole signal and nothing else
// fails, so a rename has to be noisy while developing.
if (process.env.NODE_ENV !== 'production' && devicesApi?.endpoints) {
  DEVICE_DATA_ENDPOINTS.filter((name) => !devicesApi.endpoints[name]).forEach((name) => {
    console.error(
      `useDeviceStatusLoading: devicesApi has no "${name}" endpoint, so device data loading can never be reported.`,
    );
  });
}

const SETTLED_STATUSES = ['fulfilled', 'rejected'];

// Ids reach us as devices, as bare ids, or as a mix of both, and the API accepts either
// strings or numbers for `ids`, so compare them as strings.
function toIdList(value) {
  if (value === null || value === undefined || value === '') return null;

  return (Array.isArray(value) ? value : [value]).map((entry) => {
    const id = entry !== null && typeof entry === 'object' ? entry.id : entry;
    return id === null || id === undefined ? undefined : String(id);
  });
}

// Does this device-data request say anything about the device(s) being rendered?
// `ids` is optional: without it the request covers every device, with it only the listed
// ones — exporting one device must not push every other row into "Loading".
function queryCoversTarget(query, targetIds) {
  const queryIds = toIdList(query?.originalArgs?.ids);
  if (queryIds === null) return true; // a full-list fetch covers every device
  if (targetIds === null) return true; // caller asked about any device at all

  return targetIds.some((id) => id !== undefined && queryIds.includes(id));
}

// True only while a request that covers these device(s) is outstanding AND no such
// request has come back yet. Once a covering request has settled the status is known —
// the device either received points or genuinely has none — so a later request (a new
// chart time range, another device's export) must never send it back to "Loading" and
// hide a real "Offline".
function selectIsDeviceDataPending(queries = {}, targetIds) {
  let isPending = false;

  for (const query of Object.values(queries)) {
    if (!DEVICE_DATA_ENDPOINTS.includes(query?.endpointName)) continue;
    if (!queryCoversTarget(query, targetIds)) continue;
    if (SETTLED_STATUSES.includes(query.status)) return false;
    if (query.status === 'pending') isPending = true;
  }

  return isPending;
}

export function isDeviceStatusLoading(device, isDataLoading) {
  const deviceHasLatestPoint = Array.isArray(device?.data)
    ? device.data.length > 0
    : Boolean(device?.data);
  const isCameraDevice =
    device?.device_type === 'Overwatch VMS' ||
    device?.device_type === 'Exacq Vision' ||
    device?.system_type === 'Exacq Vision';

  return Boolean(isDataLoading && !deviceHasLatestPoint && !isCameraDevice);
}

/**
 * Is device data still on its way for the given device(s)?
 *
 * @param {object|number|string|Array} [target] a device, a device id, or a list of
 *   either. Omit it to ask about any device at all.
 */
export function useDeviceDataLoading(target) {
  // Only the FIRST load of the device list leaves a status unknown. A background refetch
  // (any `invalidatesTags: ['Device']` mutation — saving flood settings, linking a siren)
  // keeps the cached data points, so it must not erase statuses that are already known.
  const { isLoading } = useGetConnectedDevicesQuery();
  const targetIds = toIdList(target);
  const isDeviceDataPending = useSelector((state) =>
    selectIsDeviceDataPending(state?.[devicesApi.reducerPath]?.queries, targetIds),
  );

  return Boolean(isLoading || isDeviceDataPending);
}

export function useDeviceStatusLoading(device) {
  const isDataLoading = useDeviceDataLoading(device);
  return isDeviceStatusLoading(device, isDataLoading);
}
